import React, { useMemo, useRef } from 'react'
import Collection from '@jimmyboys/liita-ui/dist/components/Collection'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import Widget from '@jimmyboys/liita-ui/dist/components/Widget'

import NotificationCard, {
	NotificationCardSkeleton,
} from 'components/cards/NotificationCard/NotificationCard'
import NoData from 'components/general/NoData/NoData'
import { TrustNotification } from 'models'
import { useFetchList } from 'store'

import useCalculateTextsHeights from '@jimmyboys/liita-ui/dist/hooks/useCalculateTextsHeights'
import useRect from '@jimmyboys/liita-ui/dist/hooks/useRect'
import { LINE_SPLIT_REGEX } from 'controllers/ClinicalProcess/utils'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import LastUpdate from 'components/general/LastUpdate/LastUpdate'

import themeStyles from 'assets/styles/theme.module.sass'
import useCollectionSpecs, {
	getThemeSpacing,
	getThemeVariable,
} from 'hooks/useCollectionSpecs'

import styles from './Notifications.module.sass'
import NotificationsFilters from './NotificationsFilters'
import useQueryParams from '@jimmyboys/liita-ui/dist/hooks/useQueryParams'
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import queryString from 'query-string'
import { Options } from 'models'
import Dropdown from '@jimmyboys/liita-ui/dist/components/Dropdown'
import useSession from 'hooks/useSession'
import Datepicker, { DATE_FORMAT } from 'components/Datepicker/Datepicker'
import moment from 'moment'
import TextSearchInput from 'components/TextSearchInput/TextSearchInput'
import useTermSearch from 'hooks/useTermSearch'

const { phi, smallFontSize } = themeStyles
const [themeVerticalSpacing, themeHorizontalSpacing] = getThemeSpacing()
const themeSmallFontSize = getThemeVariable(smallFontSize)

const sortOptionsFunc = (i18n) => [
	{
		value: 'date,asc',
		text: i18n`Date, asc.`,
	},
	{
		value: 'date,desc',
		text: i18n`Date, desc.`,
	},
]

const Notifications = () => {
	const { i18n, screenSize } = useGlobal()
	const location = useLocation()
	const collectionRef = useRef(null)
	const rect = useRect(collectionRef)
	const { user: sessionUser } = useSession()
	const userRoles = sessionUser?.roles || []

	const cards = {
		NotificationCard,
		NotificationCardSkeleton,
	}

	const cardLabels = {
		options: i18n`Options`,
		unread: i18n`Mark as Unread`,
		read: i18n`Mark as Read`,
		reject: i18n`Close`,
		postpone: i18n`Postpone`,
		reply: i18n`Reply`,
		// Removed forward to backoffice, because backoffice is to be removed
		// forward: i18n`Forward to manager`,
	}

	const [queryParams, , setQueryParams] = useQueryParams(location)
	const qs = queryString.stringify(queryParams)

	const {
		state,
		contents: [notificationsData],
		timestamps: [dataTimestamp],
		reload,
	} = useFetchList(
		[`/notifications${qs ? `?${qs}` : ''}`],
		[TrustNotification.fromJSON]
	)

	const {
		contents: [trustManagers],
	} = useFetchList(
		['/metadata/trust_managers'],
		[Options.fromSimpleObjectJSON]
	)

	const isLoading = state === 'loading'

	const data = (notificationsData || []).map((json) => {
		return {
			...json,
			$cardName: 'NotificationCard',
			$applyCardBackground: true,
			$cardLabels: cardLabels,
		}
	})

	const textSpecs = useCollectionSpecs(
		collectionRef,
		rect,
		themeHorizontalSpacing,
		themeSmallFontSize
	)
	const cardMargin = themeVerticalSpacing / phi / phi

	const isBeforeTextComputation = !(textSpecs.fontSize && textSpecs.maxWidth)

	const textsHeights = useCalculateTextsHeights(
		isBeforeTextComputation ? [] : data,
		textSpecs.fontSize
			? `${textSpecs.fontWeight} ${textSpecs.fontSize}px ${
					textSpecs.fontFamily || ''
			  }`
			: null,
		textSpecs.letterSpacing,
		textSpecs.maxWidth,
		LINE_SPLIT_REGEX
	)

	const trustManagersFilter = useMemo(
		() =>
			Array.isArray(trustManagers)
				? [
						{
							value: '',
							text: i18n`My`,
						},
						{
							value: 'none',
							text: i18n`Without Coordinator`,
						},
						...trustManagers.filter(
							({ value }) => value !== sessionUser.legacyId
						),
				  ]
				: [],
		[i18n, sessionUser.legacyId, trustManagers]
	)

	const selectedTrustManager = queryParams.claimsManagerTrust
		? trustManagersFilter.find(
				({ value }) =>
					queryParams.claimsManagerTrust.toLocaleLowerCase() ===
					value.toLocaleLowerCase()
		  )
		: null

	const sortOptions = sortOptionsFunc(i18n)

	const selectedSort = queryParams.sort
		? sortOptions.find(
				({ value }) =>
					queryParams.sort.toLocaleLowerCase() ===
					value.toLocaleLowerCase()
		  )
		: null

	const { searchMatches, onSearch, query } = useTermSearch(data, [
		'text',
		'creationUser.name',
	])

	const [itemsHeights, collectionItems] = useMemo(() => {
		let itemsHeights = []
		let collectionItems = []
		if (isLoading) {
			const loadingItems = new Array(5).fill({})
			collectionItems = loadingItems.map(() => {
				return {
					$cardName: 'NotificationCardSkeleton',
				}
			})
			itemsHeights = loadingItems.map(() => {
				return (
					NotificationCardSkeleton.defaultHeight +
					(parseFloat(styles.rowMargin) || cardMargin)
				)
			})
		} else {
			searchMatches.forEach((item) => {
				const numberOfLines =
					item && textsHeights && textsHeights[item.textId]
						? textsHeights[item.textId]
						: 0
				const lineHeight = textSpecs.lineHeight || 1
				const cardDefaultLines = NotificationCard.defaultLines
				const extraLines = Math.max(0, numberOfLines - cardDefaultLines)
				const extraHeight = extraLines * lineHeight
				const cardHeight = NotificationCard.defaultHeight
				const textHeight = numberOfLines * lineHeight
				const height = cardHeight + cardMargin + extraHeight
				itemsHeights.push(height)
				collectionItems.push({
					...item,
					height,
					textHeight,
				})
			})
		}

		return [itemsHeights, collectionItems]
	}, [searchMatches, textsHeights, isLoading, textSpecs, cardMargin])

	return (
		<>
			<Widget paddings={['normal', 'normal', '']} applyMaxWidth>
				<HStack
					className={styles['header-options']}
					align='center'
					justify='space-between'
					wrap='wrap'
					gap='xsmall'
					flex={[
						[1, 1],
						[0, 0],
					]}
				>
					<HStack
						align='center'
						justify='flex-start'
						wrap='nowrap'
						gap='xsmall'
					>
						<div>
							<ActionButton
								icon='reload'
								className={styles['header-button']}
								onClick={() => {
									!isLoading && reload()
								}}
							></ActionButton>
						</div>
						<VStack>
							<small>
								<strong>{i18n`Notifications`}</strong>
							</small>
							<small className='ellipsis'>
								{i18n`Last Updates`}:{' '}
								{<LastUpdate value={dataTimestamp} />}
							</small>
						</VStack>
					</HStack>
					<HStack
						align='center'
						justify='flex-start'
						wrap={screenSize === 'large' ? 'nowrap' : 'wrap'}
						gap='xsmall'
					>
						<VStack>
							<small>
								<strong>{i18n`Processes`}</strong>
							</small>
							<small className='ellipsis'>
								{i18n`Order by`}:
							</small>
						</VStack>
						<div>
							<Dropdown
								className={styles['options-entry']}
								name='sort'
								value={
									selectedSort
										? selectedSort.value
										: sortOptions[0].value
								}
								options={sortOptions}
								onChange={(e) => {
									setQueryParams({
										page: undefined,
										segment: undefined,
										sort: e.target.value,
									})
								}}
							/>
						</div>
						<Datepicker
							className={styles['date-picker']}
							placeholder={i18n.translate(DATE_FORMAT)}
							showFormat={false}
							initialDate={
								queryParams.date
									? moment(
											queryParams.date,
											DATE_FORMAT,
											true
									  )
									: undefined
							}
							type='date'
							text={i18n`date`}
							onChange={(_, originalValue) => {
								if (originalValue.isValid?.()) {
									setQueryParams({
										date: originalValue.format(DATE_FORMAT),
									})
								} else {
									setQueryParams({
										...queryParams,
										date: undefined,
									})
								}
							}}
						/>
						<HStack
							className={styles['search-input']}
							align='center'
							justify='flex-start'
							wrap='nowrap'
							gap='xsmall'
						>
							<small>
								<strong>{i18n`Search`}</strong>
							</small>
							<TextSearchInput
								placeholder={i18n`Search by terms`}
								className={styles['search-bar']}
								onKeyUp={(e) => onSearch(e)}
								onChange={(e) => {
									return onSearch(e)
								}}
							/>
						</HStack>
						{!userRoles.includes('doctor') ? (
							trustManagersFilter.length ? (
								<Dropdown
									className={styles['restrict-dropdown']}
									placeholder={i18n`See notifications`}
									name='restrict'
									searchable
									value={
										selectedTrustManager
											? selectedTrustManager.value
											: trustManagersFilter[0].value
									}
									options={trustManagersFilter}
									onChange={(e) => {
										setQueryParams({
											page: undefined,
											segment: undefined,
											claimsManagerTrust: e.target.value,
										})
									}}
								/>
							) : null
						) : null}
					</HStack>
				</HStack>
			</Widget>
			<Widget paddings={['small', 'normal', '']} applyMaxWidth>
				<NotificationsFilters notifications={data} />
			</Widget>
			<Widget paddings={['small', 'normal', '']} applyMaxWidth>
				{!isLoading && !searchMatches.length ? (
					<NoData
						applyMaxWidth
						paddings={['normal', 'normal']}
						message={
							query
								? i18n`There are no **matches**.`
								: i18n`You have no **notifications**.`
						}
					/>
				) : null}
				<Collection
					className={styles['list']}
					ref={collectionRef}
					items={collectionItems}
					renderAhead={isBeforeTextComputation ? 0 : 10}
					itemsHeights={itemsHeights}
					cards={cards}
				/>
			</Widget>
		</>
	)
}

export default Notifications
